import { useRunSummary } from "components/RunSummarization/hooks/useRunSummary";
import RunSummarizationBox from "components/RunSummarization/RunSummarizationBox";
import RunSummarizationTrigger from "components/RunSummarization/RunSummarizationTrigger";
import Box from "ds/components/Box";
import Typography from "ds/components/Typography";
import useTypedFlags from "hooks/useTypedFlags";
import { AccessResult } from "utils/access";

import PerformTaskAgainButton from "../PerformTaskAgainButton";
import RetryRunButton from "../RetryRunButton";

type UnfinishedRunActionsProps = {
  stackId: string;
  runId: string;
  canRetry: AccessResult;
  runRetryBlocker: string | null;
  isTaskRun: boolean;
  isModuleRun: boolean;
  canPerformAgain?: AccessResult;
  runTaskCommand: string | null;
};

const UnfinishedRunActions = ({
  stackId,
  runId,
  canRetry,
  runRetryBlocker,
  isTaskRun,
  isModuleRun,
  canPerformAgain,
  runTaskCommand,
}: UnfinishedRunActionsProps) => {
  const { runSummarizationAi } = useTypedFlags();
  const {
    acceptedTermsAndConditionsAI,
    canTriggerSummary,
    date,
    loading,
    showSummary,
    showTrigger,
    summary,
    summaryContainerRef,
    trigger,
  } = useRunSummary();

  const disableAiTrigger = !canTriggerSummary.result || !acceptedTermsAndConditionsAI;

  const generateTooltipMessage = () => {
    if (!acceptedTermsAndConditionsAI) {
      return "An account admin must accept the terms and conditions before using Saturnhead assist";
    }

    if (!canTriggerSummary.result && canTriggerSummary.message) {
      return canTriggerSummary.message;
    }

    return (
      <Box direction="column" gap="small">
        <Typography tag="p">Ask Saturnhead</Typography>
        <Typography tag="p">
          Saturnhead combines information from <b>all execution phases</b> to give a comprehensive
          run summary, which may take longer to process than the phase summaries.
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box gap="medium">
        {(canPerformAgain?.result || canPerformAgain?.message) && (
          <PerformTaskAgainButton
            command={runTaskCommand}
            stackId={stackId}
            errorMessage={canPerformAgain.message}
          />
        )}
        {(canRetry.result || canRetry.message || (runRetryBlocker && !isTaskRun)) && (
          <RetryRunButton
            errorMessage={canRetry.message}
            runId={runId}
            stackId={stackId}
            runRetryBlocker={runRetryBlocker}
            isModuleRun={isModuleRun}
          />
        )}
        {runSummarizationAi && showTrigger && (
          <RunSummarizationTrigger
            onPress={trigger}
            disabled={disableAiTrigger}
            tooltipText={generateTooltipMessage()}
          >
            Explain
          </RunSummarizationTrigger>
        )}
      </Box>
      {runSummarizationAi && showSummary && (
        <RunSummarizationBox
          type="run"
          ref={summaryContainerRef}
          content={summary}
          loading={loading}
          date={date}
        />
      )}
    </>
  );
};

export default UnfinishedRunActions;
